import React from "react"
import cx from "classnames"
import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { CountBadge } from "../count-badge"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabProps = {
  /**
   * Optional count rendered in a `CountBadge` after `children`.
   */
  count?: number
  /**
   * Optional icon rendered before `children`.
   */
  icon?: IconName
} & TabsPrimitive.Tab.Props

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabStyles = [
  "outline-2 outline-transparent",
  "border-b border-transparent data-[active]:border-gray-900",
  "py-5",
  "inline-flex items-center gap-x-2",
  "text-sm leading-6 font-semibold text-gray-700 data-[active]:text-gray-900",
  "transition-colors duration-200",
  "not-aria-disabled:hover:border-gray-700 data-[active]:hover:border-gray-900 focus-visible:outline-gray-900",
  "aria-disabled:text-gray-600 aria-disabled:cursor-not-allowed"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tab.
 *
 * Renders Base UI's `Tabs.Tab` as a `<button role="tab">`. Active state is
 * derived from the ancestor `PageSecondaryHeader.TabsRoot`'s `value` matching
 * this tab's `value`.
 *
 * Must be a descendant of `PageSecondaryHeader.Tabs`.
 */
export function PageSecondaryHeaderTab({
  children,
  className,
  count,
  icon,
  value,
  ...props
}: PageSecondaryHeaderTabProps) {
  return (
    <TabsPrimitive.Tab
      value={value}
      className={cx(pageSecondaryHeaderTabStyles, className)}
      {...props}
    >
      {icon && <Icon name={icon} size="xs" />}
      {children}
      {count !== undefined && <CountBadge count={count} />}
    </TabsPrimitive.Tab>
  )
}
